import React, { useEffect, useMemo, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useAuth } from "@context/auth-context";
import SubscriptionPricingCard from "@components/display/PricingCard/SubscriptionPricingCard/SubscriptionPricingCard";
import SubscriptionToggleSwitch from "@components/controls/SubscriptionToggleSwitch/SubscriptionToggleSwitch";
import { getCustomerPortal, getSubscriptionPlans, SubscriptionPlan, SubscriptionType, validatePromoCode } from "@services/paymentService";
import { redirectToSubscriptionCheckout } from "@util/stripe-checkout";
import { getRootDomain } from "@util/domain";
import styles from "./SubscribePage.module.css";

const CORE_FEATURES = ["Unlimited Funeral Notices", "Branded Memorials", "Website Management & Hosting"];

const FAQS = [
    {
        question: "Are there any lock-in periods?",
        answer: "No. You can cancel at any time. There are no long-term contracts.",
    },
    {
        question: "Is there any risk in getting started?",
        answer: "If after 30 days you don't see value, we'll refund the platform fee.",
    },
    {
        question: "What happens if we decide to no longer use the platform?",
        answer: "All existing memorials remain live to avoid disruption to families. Your funeral business branding and commission apply only while your subscription is active",
    },
    {
        question: "When are my monthly memorial credits added?",
        answer: "Your monthly memorial credits are automatically added to your account on the 1st of each month",
    },
    {
        question: "Do unused memorial credits roll over?",
        answer: "No. Memorial credits are valid for the month in which they are issued and expire at the end of that month if unused.",
    },
    {
        question: "What happens if I use more memorials than my plan includes?",
        answer: "If you create more memorials than your monthly plan includes, additional memorials are automatically billed at your plan's Additional Memorial Rate. There is no interruption to your service or access.",
    },
];

const formatPrice = (cents: number) => Math.round(cents / 100);

// Annual pricing displays as an effective monthly price snapped to the nearest charm
// price ending in 9 (e.g. $269.10 or $270 -> $269), so Stripe rounding can't surface
// an off-looking number. Display only - checkout charges the actual Stripe price.
const charmPrice = (cents: number) => (Math.round((cents / 100 - 9) / 10) * 10) + 9;

const SubscribePage = () => {
    const { authState, isSubscribed } = useAuth();
    const [isAnnual, setIsAnnual] = useState(false);
    const [coupon, setCoupon] = useState<string | undefined>();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const { user } = authState;

    useEffect(() => {
        async function loadPlans() {
            try {
                const fetchedPlans = await getSubscriptionPlans();
                setPlans(fetchedPlans);
            } catch {
                // Leave plans empty - the page will just show the Enterprise Plus contact footer
            }
        }
        void loadPlans();
    }, []);

    useEffect(() => {
        async function process() {
            if ((user?.userType === "business_admin") && (user?.subscription === "none")) {
                const retrievedCoupon = localStorage.getItem("coupon");
                if (retrievedCoupon) {
                    const result = await validatePromoCode(retrievedCoupon);
                    if (result.isValid) {
                        setCoupon(retrievedCoupon);
                    } else {
                        localStorage.removeItem("coupon");
                    }
                }
            }
        }
        void process();
    }, [coupon, user?.subscription, user?.userType]);

    const handleSubscribe = async (subscriptionType: SubscriptionType, annual: boolean) => {
        try {
            if (isSubscribed) {
                // If user is subscribed, take them to the Stripe portal to manage/switch/cancel their subscription
                const portalUrl = await getCustomerPortal();
                window.location.href = portalUrl;
            } else if (coupon) {
                await redirectToSubscriptionCheckout("essential", "monthly", coupon); // Hardcode free trial to essential monthly
            } else {
                await redirectToSubscriptionCheckout(subscriptionType, annual ? "yearly" : "monthly");
            }
        } catch (error) {
            alert("Error subscribing. Please try again.");
        }
    };

    const buttonTextFor = useMemo(() => (plan: SubscriptionPlan) => {
        if (coupon) {
            return "Claim Your Free Trial!";
        }
        if (isSubscribed) {
            return user?.subscription === plan.key ? "Your Current Plan" : "Manage Subscription";
        }
        return "Join";
    }, [coupon, isSubscribed, user?.subscription]);

    return (
        <div className={commonStyles.container}>
            <h1>Choose Your Subscription Plan</h1>
            <p className={styles.subtitle}>Select the plan that best matches your monthly memorial volume.</p>
            <div className={styles.contentContainer}>
                <div className={styles.toggleRow}>
                    <SubscriptionToggleSwitch
                        isAnnual={isAnnual}
                        onToggle={setIsAnnual}
                    />
                    <span className={`${styles.saveBadge} ${isAnnual ? "" : styles.saveBadgeInactive}`}>
                        Save 10%
                    </span>
                </div>
                <div className={styles.cardsContainer}>
                    {plans.map((plan) => {
                        // Essential has no monthly option - it's always actually billed yearly
                        const isForcedAnnual = plan.monthlyPriceCents === undefined;
                        const showAnnual = isAnnual || isForcedAnnual;
                        // Prices always display as a per-month figure. Annual billing shows the
                        // effective monthly price (yearly price / 12, charm-rounded) with a
                        // "Billed Annually" note, rather than the full yearly price the checkout
                        // actually charges.
                        const price = showAnnual
                            ? charmPrice(plan.yearlyPriceCents / 12)
                            : formatPrice(plan.monthlyPriceCents as number);
                        const hasMonthlyAllocation = plan.monthlyCreditAllocation > 0;
                        const creditsFeature = hasMonthlyAllocation
                            ? `${plan.monthlyCreditAllocation} Memorials / Month`
                            : "Pay as you go";
                        const additionalMemorialRate = `$${(plan.additionalMemorialRateCents / 100).toFixed(0)} each`;
                        const overageFeature = hasMonthlyAllocation
                            ? `Additional Memorials: ${additionalMemorialRate}`
                            : `Memorials: ${additionalMemorialRate}`;

                        // Whenever the displayed per-month price is actually billed yearly
                        // (annual toggle, or Essential on either toggle), say so.
                        const note = showAnnual ? "Billed Annually" : undefined;

                        return (
                            <div className={styles.card} key={plan.key}>
                                <SubscriptionPricingCard
                                    title={plan.displayName}
                                    boldedFeatures={[creditsFeature]}
                                    features={[...CORE_FEATURES, overageFeature]}
                                    price={price}
                                    period="month"
                                    buttonText={buttonTextFor(plan)}
                                    onButtonClick={() => handleSubscribe(plan.key, showAnnual)}
                                    note={note}
                                    variant={isAnnual ? "submit" : "primary"}
                                    isPopular={plan.isPopular}
                                    disableButton={isSubscribed && (user?.subscription === plan.key)}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className={styles.enterprisePlusFooter}>
                    <p className={styles.enterprisePlusHeading}>Need more than 40 memorials per month?</p>
                    <p>
                        {"Contact us for a customised "}
                        <strong>Enterprise Plus</strong>
                        {" plan with tailored monthly memorial credits and volume pricing."}
                    </p>
                </div>
            </div>
            <div className={styles.faqSection}>
                <h2 className={styles.faqTitle}>FAQs</h2>
                {FAQS.map(({ question, answer }) => (
                    <div className={styles.faqItem} key={question}>
                        <p className={styles.faqQuestion}>{question}</p>
                        <p className={styles.faqAnswer}>{answer}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SubscribePage;
